import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-iphone',
  category: 'device',
  kicker: 'iPhone · iPad',
  title: 'How to block porn on iPhone so it actually stays blocked',
  seoTitle: 'How to Block Porn on iPhone in 2026 (So It Stays Blocked)',
  description: `Screen Time, a DNS profile, or a supervised iPhone: three ways to block porn on iOS, what each one holds against, and the ten-minute setup that actually stays.`,
  published: '2026-08-25',
  modified: '2026-09-03',
  minutes: 8,
  intro: `You have probably already blocked porn on your iPhone once. Maybe
          twice. It held for a while, and then one tired evening you found
          the way around it — because you were the one who set it up, and
          you knew exactly where the key was. This guide goes through the
          three ways to block porn on iOS, from the weakest to the one that
          holds, and tells you plainly what each one is worth.`,
  next: ['porn-blocker-you-cant-uninstall', 'is-screen-time-enough-to-block-porn', 'does-incognito-bypass-porn-blockers'],
  body: (
    <>
      <h2>First, the question that decides everything</h2>
      <p>
        Every method below answers one question differently: <b>who holds the
        key?</b> If the answer is "you", the block holds as long as you feel
        fine — and stops holding at the exact hour you don't. That is not a
        character flaw. At two in the morning, tired and alone, you are simply
        not the person who set the block up this morning. A blocker built for
        that person has to be one that the other one cannot undo.
      </p>

      <h2>Method 1 — Screen Time (free, already on your phone)</h2>
      <p>Apple ships a content filter with every iPhone. To turn it on:</p>
      <ol>
        <li>Open <b>Settings → Screen Time</b> and turn it on if it isn't.</li>
        <li>Tap <b>Content &amp; Privacy Restrictions</b> and enable them.</li>
        <li>Go to <b>Content Restrictions → Web Content</b> and choose <b>Limit Adult Websites</b>.</li>
        <li>Back in Screen Time, set a <b>Screen Time passcode</b> — a four-digit code separate from your unlock code.</li>
      </ol>
      <p>
        What it does well: it filters adult sites in Safari, and it removes
        Safari's Private Browsing while it's on. It costs nothing and takes
        two minutes.
      </p>
      <p>
        Where it stops: <b>you know the passcode.</b> Even if you make someone
        else set it, Apple lets a Screen Time passcode be reset with the Apple
        ID on the phone — yours. The filter is also a website filter: it does
        not look inside apps, and it does nothing on the Mac or the PC in the
        next room unless you set it there too. In practice, Screen Time is a
        speed bump. It stops you on a good day. We wrote a separate piece on
        exactly <Link to="/guides/is-screen-time-enough-to-block-porn">how
        Screen Time gets bypassed</Link>.
      </p>

      <h2>Method 2 — A DNS filter (a profile, or a filtering app)</h2>
      <p>
        A DNS filter works one level below the browser. Every time any app on
        the phone asks "where is this site?", the answer comes from a
        filtering resolver that refuses whole categories — pornography,
        nudity, adult themes. It doesn't matter which browser you use, or
        whether it's in private mode: the site never resolves.
      </p>
      <p>
        On iPhone you get this by installing a configuration profile that
        sets an encrypted DNS resolver, or by installing an app that does the
        same thing through a VPN-style tunnel. Both cover every app on the
        phone, which already beats Screen Time.
      </p>
      <p>
        Where it stops: a profile you installed is a profile you can remove.
        <b>Settings → General → VPN &amp; Device Management → Remove
        Profile.</b> Four taps. An app-based filter is deleted like any app.
        The category filter is the right tool; the problem is that it is
        still attached to the phone with a sticky note.
      </p>

      <h2>Method 3 — A supervised iPhone (the one that stays)</h2>
      <p>
        Supervision is an Apple mechanism designed for organisations that
        hand out iPhones and need settings to stick. A supervised phone can
        carry a configuration profile that <b>cannot be removed from the
        phone itself</b>. The Remove Profile button simply isn't there. And
        the same profile can forbid adding a VPN, which closes the usual back
        door.
      </p>
      <p>
        That is how SELFCNTRL works on iPhone and iPad. The filter is the DNS
        category filter from method 2; supervision is what keeps it on. To
        take it off, you don't tap a button at 2am — you make a deliberate
        request from your account, and it takes effect after a cooling-off
        delay. You stay free to stop. You just can't stop on an impulse.
      </p>
      <p>Setting it up takes about ten minutes, once:</p>
      <ol>
        <li><Link to="/register">Create your space</Link> — one email address, nothing else.</li>
        <li>Plug the iPhone into a Mac or a Windows PC with a cable and run the SELFCNTRL supervisor. It puts the phone in supervised mode. This is the only step that needs a computer.</li>
        <li>From your account, scan a QR code with the phone's camera. The profile installs and seals itself.</li>
        <li>Pick your pace — cut it dead, or four locked days a week that climb to seven — and, if you want, a night curfew that cuts the internet entirely between, say, 11pm and 7am.</li>
      </ol>
      <p>
        After that there is nothing to open every day, no password to
        remember, and no fight to win at night. Tightening the block takes
        effect at once. Loosening it waits twenty-four hours, and the floor
        never drops below where you set it.
      </p>

      <h2>What supervision does not do</h2>
      <p>
        People hear "supervised" and picture a company phone. It isn't that.
        Supervision here does one thing: keep the filter from being taken
        off. Nobody reads your messages or photos, no one can wipe the phone
        or see where it is, and no report goes to anyone — filtering works on
        categories, not on a list of what you looked at, so there is nothing
        to report. Your history stays yours. It is written out plainly in
        the <Link to="/confidentialite">privacy policy</Link>.
      </p>

      <h2>The three methods, side by side</h2>
      <table>
        <thead>
          <tr><th>Question</th><th>Screen Time</th><th>DNS profile</th><th>Supervised</th></tr>
        </thead>
        <tbody>
          <tr><td>Covers every app, not just Safari</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Covers private browsing</td><td>Yes (disables it)</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Removable in under a minute</td><td>Yes (passcode or Apple ID reset)</td><td>Yes (Remove Profile)</td><td>No</td></tr>
          <tr><td>Blocks adding a workaround VPN</td><td>No</td><td>No</td><td>Yes</td></tr>
          <tr><td>Who holds the key</td><td>You</td><td>You</td><td>A delay you set on a good day</td></tr>
        </tbody>
      </table>

      <h2>The bet</h2>
      <p>
        Every blocker claims to be unbeatable, so we put money on it: on a
        device we locked, if you manage to strip the filter off by your own
        means, we send you $100 on top of a full refund. The details are in
        the <Link to="/remboursement">refund policy</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Can I block porn on iPhone without a computer?',
      a: `Screen Time and a DNS profile need no computer, and both are worth
          turning on today. The supervised lock — the one that doesn't come
          off — needs a Mac or a Windows PC once, for about ten minutes, to
          put the phone in supervised mode. After that the computer is never
          needed again.`
    },
    {
      q: 'Does it work on iPad too?',
      a: `Yes. An iPad is supervised exactly like an iPhone, with the same
          profile and the same QR code from your account.`
    },
    {
      q: 'Will I lose my data when the phone is supervised?',
      a: `Putting an iPhone in supervised mode can involve a reset of the
          device, depending on its state. Back it up first (iCloud or a
          computer) and restore afterwards: your apps, photos and messages
          come back. Ten minutes, once. The supervisor walks you through it.`
    },
    {
      q: 'What about a new iPhone?',
      a: `A new device is untouched until you lock it too. Same three moves,
          same ten minutes, from the same account.`
    }
  ]
}

export default guide
